import React, { useState, useEffect, useRef, useMemo } from "react";
import Map, { Marker as MapMarker, Popup } from "react-map-gl/mapbox";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import Marker from "./Marker";
import Tooltip from "./Tooltip";

// Custom styles to remove Mapbox popup chrome so custom cards/chips are the only visible UI.
const popupStyle = `
    /* Name/org chip: stay under the marker avatar so the pfp always paints on top */
    .map-marker-badge-popup.mapboxgl-popup {
        z-index: 1 !important;
    }
    /* Hover tooltip: above markers */
    .map-marker-popup.mapboxgl-popup {
        z-index: 4 !important;
    }
    .mapboxgl-map .mapboxgl-marker {
        z-index: 2 !important;
    }
    .map-marker-popup .mapboxgl-popup-content,
    .map-marker-badge-popup .mapboxgl-popup-content {
        padding: 0 !important;
        background: transparent !important;
        border: none !important;
        box-shadow: none !important;
        border: none !important;
        margin: 0 !important;
    }
    .map-marker-popup .mapboxgl-popup-tip,
    .map-marker-badge-popup .mapboxgl-popup-tip {
        border: none !important;
        box-shadow: none !important;
        opacity: 0 !important;
    }
`;

// Mapbox token from environment variable
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || "";
const AUTO_BOUNDS_MAX_ZOOM = 6;
const CITY_BOUNDS_MAX_ZOOM = 12;
const DEFAULT_VIEW_STATE = {
    latitude: 39.8283,
    longitude: -98.5795,
    zoom: 2.25,
};
// Fraction of the smaller viewport dimension the globe should occupy when fully
// zoomed out. Calibrated empirically: the perspective camera renders the globe
// ~20% larger than the flat-projection estimate below, so this includes that
// correction plus some breathing room.
const GLOBE_FIT_MARGIN = 0.72;

/**
 * Base (fully zoomed out) zoom level for the current screen size.
 *
 * The globe's rendered diameter is roughly worldSize / PI = 512 * 2^zoom / PI px,
 * so this solves for the zoom where the whole globe fits within the smaller
 * viewport dimension (with a margin), capped at the desktop default so large
 * screens keep the existing framing. Used for the starting position, minZoom,
 * and idle zoom resets so they can never disagree.
 */
const getResponsiveBaseZoom = () => {
    if (typeof window === "undefined") return DEFAULT_VIEW_STATE.zoom;
    const minDimension = Math.min(window.innerWidth, window.innerHeight);
    const fitZoom = Math.log2((minDimension * Math.PI * GLOBE_FIT_MARGIN) / 512);
    return Math.min(DEFAULT_VIEW_STATE.zoom, Math.max(0, fitZoom));
};
const GROUP_PIXEL_RADIUS_MAX = 34;
const GROUP_PIXEL_RADIUS_MIN = 0;
const SPIDERFY_PIXEL_RADIUS = 46;
const SPIDER_BADGE_GAP = 6; // distance between marker icon and badge (for group spider)
const MARKER_ICON_CENTER_OFFSET_Y = 26;
const MARKER_ICON_RADIUS = 24;
const SPIDER_BADGE_VERTICAL_ANGLE_SHIFT = 12; // vertical adjustment for spider badge position
const GROUPING_DISABLE_ZOOM = 11;
/** Matches Tailwind `md` — desktop icon rail width reserved for camera centering. */
const DESKTOP_BREAKPOINT_PX = 768;

/**
 * Left camera padding so the globe centers in the area beside the glass sidebar.
 * @param {number} requested
 * @returns {number}
 */
const resolveCameraPaddingLeft = (requested) => {
    if (!requested || typeof window === "undefined") return 0;
    return window.innerWidth >= DESKTOP_BREAKPOINT_PX ? requested : 0;
};

// Calculate popup offset and anchor based on marker position relative to visible map bounds
const calculatePopupPosition = (latitude, longitude, mapRef) => {
    if (!mapRef.current) {
        return { offset: [0, -55], anchor: "bottom" }; // Default: above marker
    }

    const map = mapRef.current.getMap();
    if (!map) {
        return { offset: [0, -55], anchor: "bottom" };
    }

    // Convert lat/lng to pixel coordinates on screen
    const point = map.project([longitude, latitude]);
    
    // Get map container dimensions
    const container = map.getContainer();
    if (!container) {
        return { offset: [0, -55], anchor: "bottom" };
    }
    
    const mapWidth = container.offsetWidth || container.clientWidth;
    const mapHeight = container.offsetHeight || container.clientHeight;
    
    // Calculate edge thresholds (20% of viewport in pixels for better corner detection)
    const thresholdY = 0.50; // popup below for bottom half of map - can't be more than 0.5 obviously
    const thresholdX = 0.15;
    const topThreshold = mapHeight * thresholdY;
    const leftThreshold = mapWidth * thresholdX;
    const rightThreshold = mapWidth * (1 - thresholdX);
    
    // Determine offset and anchor based on pixel position
    let offsetX = 0;
    let offsetY = -55; // Default: above marker
    let anchor = "bottom"; // Default: popup above marker
    
    // Check if near top edge (in pixels) - show popup below
    const isNearTop = point.y <= topThreshold;
    const isNearLeft = point.x <= leftThreshold;
    const isNearRight = point.x >= rightThreshold;
    
    // For corners, check if in top-left or top-right corner region
    // Use a more aggressive check for corners - if both X and Y are in corner region
    const cornerThreshold = 0.25; // 25% for corner detection
    const topCornerThreshold = mapHeight * cornerThreshold;
    const leftCornerThreshold = mapWidth * cornerThreshold;
    const rightCornerThreshold = mapWidth * (1 - cornerThreshold);
    
    const isTopLeftCorner = point.y <= topCornerThreshold && point.x <= leftCornerThreshold;
    const isTopRightCorner = point.y <= topCornerThreshold && point.x >= rightCornerThreshold;
    
    // Show popup below if near top OR in a top corner
    if (isNearTop || isTopLeftCorner || isTopRightCorner) {
        offsetY = 0; // Below marker
        anchor = "top"; // Anchor at top so popup appears below
    }
    
    // Check if near left edge - shift right
    if (isNearLeft) {
        offsetX = 70;
    }
    // Check if near right edge - shift left
    else if (isNearRight) {
        offsetX = -70;
    }
    
    return { offset: [offsetX, offsetY], anchor };
};

const getMarkerBadgeContent = (item) => {
    if (item?.type === "location") {
        return { primary: item?.name || "Location", secondary: null };
    }

    const isUser = item?.type === "user";
    const name = isUser
        ? (item?.first_name && item?.last_name
            ? `${item.first_name} ${item.last_name}`
            : item?.username || "User")
        : (item?.name || "Organization");

    if (!isUser) {
        return { primary: name, secondary: null };
    }

    const firstAffiliation = item?.affiliations && item.affiliations.length > 0
        ? item.affiliations[0]
        : null;
    const organizationName = firstAffiliation?.organization?.name || null;

    return { primary: name, secondary: organizationName };
};

const markerIdentity = (item) => {
    if (!item) return "";
    return `${item.type || "unknown"}-${item.id ?? "no-id"}-${item.latitude}-${item.longitude}`;
};

const getGroupingPixelRadiusForZoom = (zoom) => {
    const numericZoom = Number.isFinite(zoom) ? zoom : DEFAULT_VIEW_STATE.zoom;
    if (numericZoom >= GROUPING_DISABLE_ZOOM) {
        return 0;
    }
    // Larger merge radius when zoomed out; tighter radius when zoomed in.
    const scaled = GROUP_PIXEL_RADIUS_MAX - (numericZoom - 2.5) * 2.2;
    return Math.max(GROUP_PIXEL_RADIUS_MIN, Math.min(GROUP_PIXEL_RADIUS_MAX, scaled));
};

const buildMarkerGroups = (items, map, pixelRadius) => {
    if (!items || items.length === 0) return [];
    if (!map) {
        return items.map((item) => ({
            id: markerIdentity(item),
            items: [item],
            latitude: item.latitude,
            longitude: item.longitude,
        }));
    }

    const projected = items.map((item) => ({
        item,
        point: map.project([item.longitude, item.latitude]),
    }));
    const used = new Array(projected.length).fill(false);
    const groups = [];

    for (let i = 0; i < projected.length; i += 1) {
        if (used[i]) continue;

        const queue = [i];
        const groupIndices = [];
        used[i] = true;

        while (queue.length > 0) {
            const currentIndex = queue.shift();
            groupIndices.push(currentIndex);
            const currentPoint = projected[currentIndex].point;

            for (let j = 0; j < projected.length; j += 1) {
                if (used[j]) continue;
                const comparePoint = projected[j].point;
                const dx = currentPoint.x - comparePoint.x;
                const dy = currentPoint.y - comparePoint.y;
                const distance = Math.sqrt(dx * dx + dy * dy);
                if (distance <= pixelRadius) {
                    used[j] = true;
                    queue.push(j);
                }
            }
        }

        const groupItems = groupIndices.map((index) => projected[index].item);
        const latTotal = groupItems.reduce((sum, item) => sum + item.latitude, 0);
        const lonTotal = groupItems.reduce((sum, item) => sum + item.longitude, 0);
        const sortedIds = groupItems.map((item) => markerIdentity(item)).sort();
        groups.push({
            id: sortedIds.join("|"),
            items: groupItems,
            latitude: latTotal / groupItems.length,
            longitude: lonTotal / groupItems.length,
        });
    }

    return groups;
};

const getSpiderfiedMarkers = (group, map, pixelRadius) => {
    if (!group || !map) return [];
    const count = group.items.length;
    if (count <= 1) {
        return [{
            ...group.items[0],
            latitude: group.items[0].latitude,
            longitude: group.items[0].longitude,
        }];
    }

    const centerPoint = map.project([group.longitude, group.latitude]);
    return group.items.map((item, index) => {
        const angle = (Math.PI * 2 * index) / count;
        const x = centerPoint.x + Math.cos(angle) * pixelRadius;
        const y = centerPoint.y + Math.sin(angle) * pixelRadius;
        const lngLat = map.unproject([x, y]);
        return {
            ...item,
            latitude: lngLat.lat,
            longitude: lngLat.lng,
            _spiderSourceIdentity: markerIdentity(item),
            _spiderGroupCenterLat: group.latitude,
            _spiderGroupCenterLng: group.longitude,
        };
    });
};

const getSpiderBadgePosition = (item, mapRef, badgeSize) => {
    const map = mapRef.current?.getMap();
    if (!map || item?._spiderGroupCenterLat == null || item?._spiderGroupCenterLng == null) {
        return { offset: [0, -42], anchor: "bottom" };
    }

    const markerPoint = map.project([item.longitude, item.latitude]);
    const centerPoint = map.project([item._spiderGroupCenterLng, item._spiderGroupCenterLat]);
    const dx = markerPoint.x - centerPoint.x;
    const dy = markerPoint.y - centerPoint.y;
    const magnitude = Math.sqrt(dx * dx + dy * dy);
    const nx = magnitude > 0 ? dx / magnitude : 0;
    const ny = magnitude > 0 ? dy / magnitude : -1;

    const width = badgeSize?.width || 140;
    const height = badgeSize?.height || 44;
    const halfWidth = width / 2;
    const halfHeight = height / 2;

    // Projection of half-size on radial vector gives exact center offset needed
    // so chip edge stays a constant gap from the pfp icon regardless of angle.
    const chipHalfExtentOnVector = Math.abs(nx) * halfWidth + Math.abs(ny) * halfHeight;
    const radialDistanceFromPfpCenter = MARKER_ICON_RADIUS + SPIDER_BADGE_GAP + chipHalfExtentOnVector;

    const popupCenterX = markerPoint.x + nx * radialDistanceFromPfpCenter;
    const popupCenterY = (markerPoint.y - MARKER_ICON_CENTER_OFFSET_Y) + ny * radialDistanceFromPfpCenter;
    const baseOffsetX = Math.round(popupCenterX - markerPoint.x);
    const baseOffsetY = Math.round(popupCenterY - markerPoint.y);

    // Post-position angle-based vertical correction:
    // above (ny < 0) shifts down, below (ny > 0) shifts up, sides (~0) unchanged.
    const verticalAngleShift = Math.round(ny * SPIDER_BADGE_VERTICAL_ANGLE_SHIFT);

    return {
        anchor: "center",
        offset: [
            baseOffsetX,
            baseOffsetY - verticalAngleShift,
        ],
    };
};

const spiderBadgeSizeKey = (item) => {
    if (!item) return "";
    return item._spiderSourceIdentity || markerIdentity(item);
};

const didBadgeSizeChange = (prev, next) => {
    if (!prev) return true;
    return prev.width !== next.width || prev.height !== next.height;
};

const updateSpiderBadgeSize = (node, key, setSpiderBadgeSizes) => {
    if (!node || !key) return;
    const rect = node.getBoundingClientRect();
    const nextSize = {
        width: Math.round(rect.width),
        height: Math.round(rect.height),
    };
    setSpiderBadgeSizes((prev) => {
        const previousSize = prev[key];
        if (!didBadgeSizeChange(previousSize, nextSize)) {
            return prev;
        }
        return {
            ...prev,
            [key]: nextSize,
        };
    });
};

/**
 * Reads a CSS custom property from the document root.
 * @param {CSSStyleDeclaration} root
 * @param {string} name
 * @returns {string}
 */
const readThemeColor = (root, name) => root.getPropertyValue(name).trim();

/**
 * Applies theme CSS variables to Mapbox light-v11 basemap layers
 * (land/water fills, roads, and labels).
 * @param {import("mapbox-gl").Map} map
 * @returns {void}
 */
const applyThemedMapBasemapFills = (map) => {
    if (!map?.getStyle) return;
    const style = map.getStyle();
    if (!style?.layers) return;

    const root = getComputedStyle(document.documentElement);
    const ocean = readThemeColor(root, "--color-map-ocean");
    const land = readThemeColor(root, "--color-map-land");
    const park = readThemeColor(root, "--color-map-park") || land;
    const wood = readThemeColor(root, "--color-map-wood") || park;
    const grass = readThemeColor(root, "--color-map-grass") || park;
    const sand = readThemeColor(root, "--color-map-sand") || land;
    const rock = readThemeColor(root, "--color-map-rock") || land;
    const glacier = readThemeColor(root, "--color-map-glacier") || land;
    const urban = readThemeColor(root, "--color-map-urban") || land;
    const building = readThemeColor(root, "--color-map-building") || urban;
    const aeroway = readThemeColor(root, "--color-map-aeroway") || urban;
    const landStructure = readThemeColor(root, "--color-map-land-structure") || land;
    const road = readThemeColor(root, "--color-map-road");
    const roadCase = readThemeColor(root, "--color-map-road-case") || road;
    const roadMinor = readThemeColor(root, "--color-map-road-minor") || road;
    const label = readThemeColor(root, "--color-map-label");
    const labelHalo = readThemeColor(root, "--color-map-label-halo");

    if (!ocean && !land) return;

    const layers = style.layers;
    const setPaint = (layerId, paintProp, value) => {
        if (value == null || value === "" || !map.getLayer(layerId)) return;
        const layer = layers.find((l) => l.id === layerId);
        if (!layer) return;
        try {
            map.setPaintProperty(layerId, paintProp, value);
        } catch {
            /* composite or read-only layer */
        }
    };

    // Base globe: land is type "background"; water is fill
    setPaint("land", "background-color", land);
    setPaint("water", "fill-color", ocean);
    setPaint("water-shadow", "fill-color", ocean);
    setPaint("waterway", "line-color", ocean);

    setPaint("national-park", "fill-color", park);
    // Default light-v11 opacities are very low; raise so themed fills read clearly.
    setPaint("national-park", "fill-opacity", [
        "interpolate",
        ["linear"],
        ["zoom"],
        5,
        0,
        6,
        0.55,
        12,
        0.4,
    ]);
    setPaint("land-structure-polygon", "fill-color", landStructure);
    setPaint("land-structure-line", "line-color", landStructure);
    setPaint("aeroway-polygon", "fill-color", aeroway);
    setPaint("aeroway-line", "line-color", aeroway);
    setPaint("building", "fill-color", building);
    setPaint("building", "fill-outline-color", urban);

    // landuse classes present in light-v11 (vegetation, dry, ice, urban)
    if (map.getLayer("landuse")) {
        // light-v11 hides residential at zoom ≥ 12 (green land shows through). Keep cities grey.
        try {
            map.setFilter("landuse", [
                "all",
                [">=", ["to-number", ["get", "sizerank"]], 0],
                [
                    "match",
                    ["get", "class"],
                    [
                        "agriculture",
                        "wood",
                        "grass",
                        "scrub",
                        "park",
                        "airport",
                        "glacier",
                        "pitch",
                        "sand",
                        "residential",
                    ],
                    true,
                    false,
                ],
                [
                    "<=",
                    [
                        "-",
                        ["to-number", ["get", "sizerank"]],
                        [
                            "interpolate",
                            ["exponential", 1.5],
                            ["zoom"],
                            12,
                            0,
                            18,
                            14,
                        ],
                    ],
                    8,
                ],
            ]);
        } catch {
            /* filter unsupported */
        }

        setPaint("landuse", "fill-color", [
            "match",
            ["get", "class"],
            "wood",
            wood,
            "park",
            park,
            ["grass", "scrub", "agriculture", "pitch"],
            grass,
            "sand",
            sand,
            "rock",
            rock,
            "glacier",
            glacier,
            ["residential", "airport"],
            urban,
            land,
        ]);
        setPaint("landuse", "fill-opacity", [
            "interpolate",
            ["linear"],
            ["zoom"],
            5,
            [
                "match",
                ["get", "class"],
                ["residential", "airport"],
                0.85,
                0.4,
            ],
            10,
            [
                "match",
                ["get", "class"],
                ["residential", "airport"],
                0.95,
                0.65,
            ],
            14,
            [
                "match",
                ["get", "class"],
                "residential",
                0.98,
                "airport",
                0.7,
                0.6,
            ],
        ]);
    }

    // Roads / paths / rails: major arterials darker, local streets lighter (Google-like hierarchy)
    if (road) {
        const roadByClass = [
            "match",
            ["get", "class"],
            ["motorway", "trunk", "primary", "motorway_link", "trunk_link", "primary_link"],
            road,
            ["secondary", "tertiary", "secondary_link", "tertiary_link"],
            road,
            roadMinor,
        ];
        for (const layer of layers) {
            if (layer.type !== "line") continue;
            const id = layer.id;
            if (!/^(road|tunnel|bridge)-/.test(id)) continue;
            if (id.includes("case")) {
                setPaint(id, "line-color", roadCase);
                continue;
            }
            // Layers that carry road class get hierarchy; path/rail stay mid-weight
            if (
                id === "road-simple" ||
                id === "tunnel-simple" ||
                id === "bridge-simple" ||
                id === "road-pedestrian" ||
                id === "tunnel-pedestrian" ||
                id === "bridge-pedestrian"
            ) {
                setPaint(id, "line-color", roadByClass);
            } else {
                setPaint(id, "line-color", roadMinor);
            }
        }
    }

    // Place / road / POI labels: dark fill + thin land-colored halo
    if (label) {
        for (const layer of layers) {
            if (layer.type !== "symbol") continue;
            setPaint(layer.id, "text-color", label);
            if (labelHalo) {
                setPaint(layer.id, "text-halo-color", labelHalo);
                setPaint(layer.id, "text-halo-width", 0.75);
                setPaint(layer.id, "text-halo-blur", 0.2);
            }
        }
    }
};

/**
 * @param {Object} props
 * @param {number|string} props.userId
 * @param {Object|null} [props.user]
 * @param {Array<Object>} [props.results]
 * @param {{ lat: number, lon: number }} [props.loc]
 * @param {boolean} [props.readOnly] When true, disables pan/zoom/keyboard/touch map handlers (e.g. home hero).
 * @param {"default"|"hero"} [props.embedVariant] Layout: hero fills parent height edge-to-edge; default fills parent height as a rounded card.
 * @param {boolean} [props.idleSpotlightPins] When true, pins are decorative (idle sample users): keep globe auto-rotation and do not fit bounds to markers.
 * @param {{ west: number, south: number, east: number, north: number }|null} [props.cameraBounds] Fit the camera to this box (city search) instead of markers.
 * @param {number} [props.cameraPaddingLeft] Extra left camera padding (px) so the globe centers in the visible area beside a glass sidebar.
 * @param {boolean} [props.active] When false, map stays mounted but hidden — pause autorotation (no background tile fetches) and resize when shown again.
 * @param {number} [props.topOffsetPx] Shell header offset; container height changes on login/logout — trigger resize.
 */
const FlatMap = ({
    userId: _userId,
    user: _user,
    results,
    loc,
    readOnly = false,
    embedVariant = "default",
    idleSpotlightPins = false,
    cameraBounds = null,
    cameraPaddingLeft = 0,
    active = true,
    topOffsetPx = 0,
}) => {
    const mapRef = useRef(null);
    const hoverTimeoutRef = useRef(null);
    const rotationAnimationRef = useRef(null);
    const rotationResumeTimeoutRef = useRef(null);
    const userInteractedRef = useRef(false);
    const prevIdleSpotlightPinsRef = useRef(idleSpotlightPins);
    const prevReadOnlyRef = useRef(readOnly);
    /** Survives effect re-runs so explore→home zoom-out is not lost mid-transition. */
    const pendingHomeIdleResetRef = useRef(false);
    const [hoveredMarker, setHoveredMarker] = useState(null);
    const [expandedGroupId, setExpandedGroupId] = useState(null);
    const [spiderBadgeSizes, setSpiderBadgeSizes] = useState({});

    // Fully-zoomed-out level for this screen size; also the map's minZoom.
    const [baseZoom, setBaseZoom] = useState(getResponsiveBaseZoom);
    const lastZoomRef = useRef(baseZoom);
    // The minZoom PROP must stay constant for the map's lifetime: react-map-gl
    // re-wraps the map transform in a new proxy whenever a settings prop (like
    // minZoom) changes, which corrupts the transform and crashes with
    // "Maximum call stack size exceeded" in _calcMatrices on the next resize.
    // Later min zoom changes are applied imperatively via map.setMinZoom().
    const initialBaseZoomRef = useRef(baseZoom);

    /**
     * Keep Mapbox camera padding in sync so the globe centers beside the rail.
     * @param {import("mapbox-gl").Map} [mapInstance]
     */
    const applyCameraPadding = (mapInstance) => {
        const map = mapInstance || mapRef.current?.getMap();
        if (!map || typeof map.setPadding !== "function") return;
        const left = resolveCameraPaddingLeft(cameraPaddingLeft);
        map.setPadding({ top: 0, bottom: 0, left, right: 0 });
    };

    // Default viewport: US center, zoomed out to fit the screen size
    const [viewState, setViewState] = useState(() => ({
        ...DEFAULT_VIEW_STATE,
        zoom: getResponsiveBaseZoom(),
    }));

    // Keep the base zoom in sync with the window size (e.g. rotation, resize)
    useEffect(() => {
        const handleResize = () => {
            const nextBaseZoom = getResponsiveBaseZoom();
            setBaseZoom(nextBaseZoom);
            const map = mapRef.current?.getMap();
            map?.setMinZoom(nextBaseZoom);
            applyCameraPadding(map);
            if (!userInteractedRef.current) {
                setViewState((prev) => ({ ...prev, zoom: nextBaseZoom }));
            }
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- applyCameraPadding reads cameraPaddingLeft + mapRef
    }, [cameraPaddingLeft]);

    useEffect(() => {
        applyCameraPadding();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cameraPaddingLeft]);

    // Filter results to get items with valid coordinates
    const markersWithCoords = useMemo(() => {
        return (results || [])
            .map(item => {
                const lat = item.latitude;
                const lon = item.longitude;
                
                if (lat != null && lon != null && 
                    typeof lat === 'number' && typeof lon === 'number' &&
                    !isNaN(lat) && !isNaN(lon)) {
                    return {
                        ...item,
                        latitude: parseFloat(lat),
                        longitude: parseFloat(lon),
                    };
                }
                return null;
            })
            .filter(item => item !== null);
    }, [results]);

    const currentMapZoom = mapRef.current?.getMap()?.getZoom() ?? viewState.zoom;
    const groupingPixelRadius = getGroupingPixelRadiusForZoom(currentMapZoom);

    const groupedMarkers = useMemo(() => {
        const map = mapRef.current?.getMap();
        return buildMarkerGroups(markersWithCoords, map, groupingPixelRadius);
    }, [markersWithCoords, viewState, groupingPixelRadius]);

    const expandedGroup = useMemo(() => {
        if (!expandedGroupId) return null;
        return groupedMarkers.find((group) => group.id === expandedGroupId) || null;
    }, [groupedMarkers, expandedGroupId]);

    const expandedSpiderMarkers = useMemo(() => {
        const map = mapRef.current?.getMap();
        if (!expandedGroup || !map) return [];
        return getSpiderfiedMarkers(expandedGroup, map, SPIDERFY_PIXEL_RADIUS);
    }, [expandedGroup, viewState]);

    const visibleUngroupedMarkers = useMemo(() => {
        if (!expandedGroup) return [];
        return expandedSpiderMarkers;
    }, [expandedGroup, expandedSpiderMarkers]);

    // Handle loc prop: center map on location if provided
    useEffect(() => {
        if (loc && loc.lat != null && loc.lon != null) {
            setViewState(prev => ({
                ...prev,
                latitude: parseFloat(loc.lat),
                longitude: parseFloat(loc.lon),
                zoom: prev.zoom, // Keep current zoom level
            }));
        }
    }, [loc]);

    const cameraBoundsKey = cameraBounds
        ? `${cameraBounds.west},${cameraBounds.south},${cameraBounds.east},${cameraBounds.north}`
        : "";

    // Fit to a committed city bounding box (already padded by 25 miles).
    useEffect(() => {
        if (!cameraBoundsKey || idleSpotlightPins || !mapRef.current) {
            return undefined;
        }
        const map = mapRef.current?.getMap();
        if (!map) {
            return undefined;
        }

        const fitCityBounds = (mapInstance) => {
            try {
                mapInstance.fitBounds(
                    [
                        [cameraBounds.west, cameraBounds.south],
                        [cameraBounds.east, cameraBounds.north],
                    ],
                    {
                        padding: { top: 50, bottom: 50, left: 50, right: 50 },
                        maxZoom: CITY_BOUNDS_MAX_ZOOM,
                        duration: 1000,
                    }
                );
            } catch (error) {
                console.error("Error fitting city bounds:", error);
            }
        };

        if (!map.isStyleLoaded()) {
            const onStyle = () => {
                const retryMap = mapRef.current?.getMap();
                if (retryMap && retryMap.isStyleLoaded()) {
                    fitCityBounds(retryMap);
                }
            };
            map.once("styledata", onStyle);
            return () => {
                map.off("styledata", onStyle);
            };
        }

        fitCityBounds(map);
        return undefined;
    }, [cameraBounds, cameraBoundsKey, idleSpotlightPins]);

    // Handle results: fit bounds to show all results with valid coordinates (not idle spotlight pins)
    useEffect(() => {
        if (cameraBoundsKey || idleSpotlightPins || !mapRef.current || !results || results.length === 0) {
            return;
        }

        // Get the underlying Mapbox GL JS map instance
        const map = mapRef.current?.getMap();
        if (!map) {
            return;
        }

        /**
         * Fit the camera to marker coordinates (must be declared before any early return
         * that registers async callbacks referencing it).
         * @param {import("mapbox-gl").Map} mapInstance
         */
        const fitBoundsToMarkers = (mapInstance) => {
            const coordinates = markersWithCoords.map((item) => [
                item.longitude,
                item.latitude,
            ]);

            if (coordinates.length === 0) {
                return;
            }

            try {
                const bounds = coordinates.reduce(
                    (nextBounds, coord) => nextBounds.extend(coord),
                    new mapboxgl.LngLatBounds(coordinates[0], coordinates[0])
                );

                mapInstance.fitBounds(bounds, {
                    // Rail offset comes from map.setPadding; keep marker inset equal here.
                    padding: { top: 50, bottom: 50, left: 50, right: 50 },
                    maxZoom: AUTO_BOUNDS_MAX_ZOOM,
                    duration: 1000,
                });
            } catch (error) {
                console.error("Error fitting bounds:", error);
            }
        };

        // Wait for map to be loaded
        if (!map.isStyleLoaded()) {
            map.once("styledata", () => {
                setTimeout(() => {
                    const retryMap = mapRef.current?.getMap();
                    if (retryMap && retryMap.isStyleLoaded()) {
                        fitBoundsToMarkers(retryMap);
                    }
                }, 100);
            });
            return;
        }

        fitBoundsToMarkers(map);
    }, [markersWithCoords, idleSpotlightPins, results, cameraBoundsKey]);

    // Cleanup on unmount — must null the rAF handle so a remount (e.g. React
    // Strict Mode) can start rotation again. cancelAnimationFrame alone leaves a
    // stale id and startRotation becomes a permanent no-op.
    useEffect(() => {
        return () => {
            if (hoverTimeoutRef.current) {
                clearTimeout(hoverTimeoutRef.current);
                hoverTimeoutRef.current = null;
            }
            if (rotationResumeTimeoutRef.current) {
                clearTimeout(rotationResumeTimeoutRef.current);
                rotationResumeTimeoutRef.current = null;
            }
            if (rotationAnimationRef.current) {
                cancelAnimationFrame(rotationAnimationRef.current);
                rotationAnimationRef.current = null;
            }
        };
    }, []);

    // Stop rotation animation
    const stopRotation = () => {
        if (rotationAnimationRef.current) {
            cancelAnimationFrame(rotationAnimationRef.current);
            rotationAnimationRef.current = null;
        }
    };

    // Start rotation animation when there are no results
    const startRotation = () => {
        if (rotationAnimationRef.current) return; // Already rotating

        const rotate = () => {
            setViewState((prev) => {
                // Wait out zoom-out without tearing down the rAF loop.
                if (prev.zoom > 3) {
                    return prev;
                }

                let newLongitude = prev.longitude + 0.1;
                if (newLongitude > 180) {
                    newLongitude = -180 + (newLongitude - 180);
                }

                return {
                    ...prev,
                    longitude: newLongitude,
                };
            });
            rotationAnimationRef.current = requestAnimationFrame(rotate);
        };

        rotationAnimationRef.current = requestAnimationFrame(rotate);
    };

    // Auto-rotate globe when idle or fully empty; stop when user interacts, hidden, or showing real search hits.
    useEffect(() => {
        const hasResults = results && results.length > 0;
        const hasMarkers = markersWithCoords.length > 0;
        const shouldAutoRotateGlobe =
            active &&
            !cameraBoundsKey &&
            (idleSpotlightPins || (!hasResults && !hasMarkers));

        // Landing / home hero: clear explore interaction and zoom out if needed.
        const enteredHomeHero = readOnly && prevReadOnlyRef.current === false;
        prevReadOnlyRef.current = readOnly;
        if (enteredHomeHero) {
            pendingHomeIdleResetRef.current = true;
        }

        if (!active) {
            stopRotation();
            prevIdleSpotlightPinsRef.current = idleSpotlightPins;
            return undefined;
        }

        if (shouldAutoRotateGlobe) {
            const resumedIdleFromSearch =
                idleSpotlightPins && prevIdleSpotlightPinsRef.current === false;
            const fullyEmpty = !hasResults && !hasMarkers;
            const homeIdleReset = pendingHomeIdleResetRef.current;

            if (fullyEmpty || resumedIdleFromSearch || homeIdleReset) {
                userInteractedRef.current = false;
            }

            const map = mapRef.current?.getMap();
            const currentZoom = map?.getZoom?.() ?? viewState.zoom;
            const needsZoomOut = currentZoom > baseZoom + 0.05;
            const shouldResetZoom =
                needsZoomOut &&
                (fullyEmpty ||
                    resumedIdleFromSearch ||
                    homeIdleReset ||
                    (idleSpotlightPins && !hasMarkers));

            if (!userInteractedRef.current) {
                if (shouldResetZoom) {
                    const center = map?.getCenter();
                    const nextLatitude = center?.lat ?? DEFAULT_VIEW_STATE.latitude;
                    const nextLongitude = center?.lng ?? DEFAULT_VIEW_STATE.longitude;
                    lastZoomRef.current = baseZoom;
                    // Ease zoom out (e.g. clearing search). Do not jumpTo — that snapped.
                    // Do not setViewState zoom immediately either (controlled props would snap);
                    // onMove syncs viewState during the ease. Page switches at full zoom
                    // never enter this branch (needsZoomOut), so rotation stays continuous.
                    if (map && typeof map.easeTo === "function") {
                        map.stop();
                        map.easeTo({
                            center: [nextLongitude, nextLatitude],
                            zoom: baseZoom,
                            duration: 1000,
                        });
                    } else {
                        setViewState((prev) => ({
                            ...prev,
                            latitude: nextLatitude,
                            longitude: nextLongitude,
                            zoom: baseZoom,
                        }));
                    }
                }

                if (homeIdleReset) {
                    pendingHomeIdleResetRef.current = false;
                }

                // Keep spinning across route prop changes; no-op if already running.
                // While zoom eases above 3, the rAF loop stays alive without advancing
                // longitude (see startRotation) — no stop/restart pause.
                startRotation();
            } else {
                stopRotation();
            }

            prevIdleSpotlightPinsRef.current = idleSpotlightPins;
            return undefined;
        }

        prevIdleSpotlightPinsRef.current = idleSpotlightPins;

        if (rotationResumeTimeoutRef.current) {
            clearTimeout(rotationResumeTimeoutRef.current);
            rotationResumeTimeoutRef.current = null;
        }

        stopRotation();

        return () => {
            stopRotation();
        };
        // viewState.zoom is read only for needsZoomOut; including it would restart this
        // effect every rotation frame.
        // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional
    }, [results, markersWithCoords, idleSpotlightPins, baseZoom, readOnly, active, cameraBoundsKey]);

    // When the layer becomes visible again or the shell header height changes, resize.
    useEffect(() => {
        if (!active) return undefined;
        const map = mapRef.current?.getMap();
        if (!map) return undefined;
        const id = requestAnimationFrame(() => {
            try {
                map.resize();
            } catch {
                /* ignore */
            }
        });
        return () => cancelAnimationFrame(id);
    }, [active, topOffsetPx]);

    useEffect(() => {
        if (!expandedGroupId) return;
        const stillExists = groupedMarkers.some((group) => group.id === expandedGroupId);
        if (!stillExists) {
            setExpandedGroupId(null);
        }
    }, [expandedGroupId, groupedMarkers]);

    // Function to set map background color to match theme (for globe view and flat map)
    const setMapBackgroundColor = (map) => {
        if (!map) return;

        // Get the computed bg-primary color from CSS
        const bgColor = getComputedStyle(document.documentElement)
            .getPropertyValue('--color-bg-primary')
            .trim();
        const bgColorSecondary = getComputedStyle(document.documentElement)
            .getPropertyValue('--color-bg-secondary')
            .trim();
        
        if (bgColor) {
            // For globe view, use setFog to set space and halo color
            try {
                const currentFog = map.getFog();
                if (currentFog) {
                    // Preserve existing fog settings, but adjust to remove white halo
                    map.setFog({
                        ...currentFog,
                        'color': bgColorSecondary, // Lower atmosphere color (matches background)
                        'high-color': bgColorSecondary, // Upper atmosphere color (matches background)
                        'horizon-blend': 0.2, // No blending at horizon to remove halo
                        'space-color': bgColor, // Space background color
                        'star-intensity': currentFog?.starIntensity || 0
                    });
                } else {
                    // If no fog exists, create fog with space and halo color
                    map.setFog({
                        'color': bgColorSecondary,
                        'high-color': bgColorSecondary,
                        'horizon-blend': 0.2,
                        'space-color': bgColor,
                        'star-intensity': 0
                    });
                }
            } catch (e) {
                // Fog unsupported: leave canvas chrome alone. Do not paint light-v11's
                // `land` background with --color-bg-primary (that layer is continents).
                console.warn('Map fog unavailable; skipping space/halo theming.', e);
            }
        }

        applyThemedMapBasemapFills(map);
    };

    /**
     * Re-apply fog / ocean / land colors when the document theme class changes
     * (and once on mount). Previously this only ran on viewState moves.
     */
    useEffect(() => {
        const applyThemeColors = () => {
            const map = mapRef.current?.getMap();
            if (!map) return;
            if (map.isStyleLoaded()) {
                setMapBackgroundColor(map);
            } else {
                map.once("styledata", () => setMapBackgroundColor(map));
            }
        };

        applyThemeColors();

        const observer = new MutationObserver(applyThemeColors);
        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ["class"],
        });
        return () => observer.disconnect();
    }, []);

    /**
     * Enables or disables Mapbox interaction handlers for read-only / hero embeds.
     * Must stay in sync when `readOnly` flips without remounting (shared map layer).
     * @param {import("mapbox-gl").Map} map
     * @param {boolean} locked
     */
    const setMapInteractionsLocked = (map, locked) => {
        if (!map) return;
        try {
            const handlers = [
                map.scrollZoom,
                map.boxZoom,
                map.dragRotate,
                map.dragPan,
                map.keyboard,
                map.doubleClickZoom,
                map.touchZoomRotate,
                map.touchPitch,
            ];
            handlers.forEach((handler) => {
                if (!handler) return;
                if (locked) handler.disable();
                else handler.enable();
            });
        } catch {
            /* ignore */
        }
    };

    // Keep pan/zoom handlers aligned with readOnly across route-driven prop changes.
    useEffect(() => {
        const map = mapRef.current?.getMap();
        if (!map) return;
        setMapInteractionsLocked(map, readOnly);
        // Container chrome (hero vs explore) can change without a window resize.
        requestAnimationFrame(() => {
            try {
                map.resize();
            } catch {
                /* ignore */
            }
        });
    }, [readOnly, embedVariant]);

    const isHero = embedVariant === "hero";
    const fallbackHeightClass = isHero
        ? "min-h-[90vh] h-full w-full"
        : "h-full w-full rounded-lg";

    // Show error message if Mapbox token is missing
    if (!MAPBOX_TOKEN) {
        return (
            <div
                className={`flex items-center justify-center border border-border bg-secondary ${fallbackHeightClass}`}
            >
                <div className="text-center p-4">
                    <p className="text-text-primary font-medium mb-2">Mapbox token not configured</p>
                    <p className="text-text-secondary text-sm">
                        Please set REACT_APP_MAPBOX_TOKEN in your environment variables.
                    </p>
                </div>
            </div>
        );
    }

    const wrapperStyle = isHero
        ? { width: "100%", height: "100%", minHeight: "100%", borderRadius: 0, overflow: "hidden" }
        : { width: "100%", height: "100%", borderRadius: "0.5rem", overflow: "hidden" };

    const wrapperClassName = `map-container-wrapper bg-primary h-full w-full${isHero ? " min-h-full" : ""}`;

    return (
        <>
            <style>{popupStyle}</style>
            <div className={wrapperClassName} style={wrapperStyle}>
                <Map
                    ref={mapRef}
                    {...viewState}
                    onMove={(evt) => {
                        // Keep controlled viewState in sync for programmatic moves
                        // (easeTo / home zoom-out) even when readOnly — interaction
                        // handlers are disabled separately via setMapInteractionsLocked.
                        if (evt.originalEvent) {
                            if (readOnly) return;
                            userInteractedRef.current = true;
                            stopRotation();
                            const hasZoomChange =
                                Math.abs((evt.viewState?.zoom ?? 0) - (lastZoomRef.current ?? 0)) >
                                0.0001;
                            if (hasZoomChange) {
                                setExpandedGroupId(null);
                                setHoveredMarker(null);
                            }
                        }
                        lastZoomRef.current = evt.viewState?.zoom ?? lastZoomRef.current;
                        setViewState(evt.viewState);
                    }}
                    onLoad={(evt) => {
                        const map = evt.target;
                        if (!map) return;

                        setMapInteractionsLocked(map, readOnly);
                        applyCameraPadding(map);

                        // Sync min zoom in case the window resized between mount and load
                        map.setMinZoom(getResponsiveBaseZoom());

                        // Ensure idle spin starts once the map is ready (covers Strict Mode
                        // remount races where the effect ran before the canvas existed).
                        if (
                            active &&
                            (idleSpotlightPins || !(results && results.length)) &&
                            !userInteractedRef.current
                        ) {
                            startRotation();
                        }

                        // Wait for style to load, then set background color
                        if (map.isStyleLoaded()) {
                            setMapBackgroundColor(map);
                        } else {
                            map.once("styledata", () => {
                                setMapInteractionsLocked(map, readOnly);
                                applyCameraPadding(map);
                                setMapBackgroundColor(map);
                            });
                        }
                    }}
                    style={{ width: "100%", height: "100%" }}
                    mapStyle="mapbox://styles/mapbox/light-v11"
                    mapboxAccessToken={MAPBOX_TOKEN}
                    attributionControl={true}
                    minZoom={initialBaseZoomRef.current}
                    maxZoom={14}
                >
                {/* Render grouped markers and expanded spiderfied markers */}
                {groupedMarkers.map((group) => {
                    if (group.items.length > 1) {
                        const isExpanded = expandedGroupId === group.id;
                        if (isExpanded) return null;

                        return (
                            <MapMarker
                                key={`group-${group.id}`}
                                latitude={group.latitude}
                                longitude={group.longitude}
                                anchor="bottom"
                            >
                                <button
                                    type="button"
                                    className="cursor-pointer flex h-12 w-12 items-center justify-center rounded-full border-2 border-accent bg-accent text-text-on-accent font-semibold shadow-lg ring-1 ring-border"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setHoveredMarker(null);
                                        setExpandedGroupId(group.id);
                                    }}
                                    aria-label={`Expand ${group.items.length} overlapping results`}
                                >
                                    {group.items.length}
                                </button>
                            </MapMarker>
                        );
                    }

                    const item = group.items[0];
                    return (
                        <MapMarker
                            key={`single-${markerIdentity(item)}`}
                            latitude={item.latitude}
                            longitude={item.longitude}
                            anchor="bottom"
                        >
                            <div
                                onMouseEnter={() => {
                                    if (hoverTimeoutRef.current) {
                                        clearTimeout(hoverTimeoutRef.current);
                                        hoverTimeoutRef.current = null;
                                    }
                                    setHoveredMarker(item);
                                }}
                                onMouseLeave={() => {
                                    hoverTimeoutRef.current = setTimeout(() => {
                                        setHoveredMarker(null);
                                        hoverTimeoutRef.current = null;
                                    }, 100);
                                }}
                            >
                                <Marker item={item} />
                            </div>
                        </MapMarker>
                    );
                })}

                {visibleUngroupedMarkers.map((item) => (
                    <MapMarker
                        key={`spider-${item._spiderSourceIdentity}`}
                        latitude={item.latitude}
                        longitude={item.longitude}
                        anchor="bottom"
                    >
                        <div
                            onMouseEnter={() => {
                                // Clear any pending hide timeout
                                if (hoverTimeoutRef.current) {
                                    clearTimeout(hoverTimeoutRef.current);
                                    hoverTimeoutRef.current = null;
                                }
                                setHoveredMarker(item);
                            }}
                            onMouseLeave={() => {
                                // Add a small delay before hiding to allow mouse to move to popup
                                hoverTimeoutRef.current = setTimeout(() => {
                                    setHoveredMarker(null);
                                    hoverTimeoutRef.current = null;
                                }, 100);
                            }}
                        >
                            <Marker item={item} />
                        </div>
                    </MapMarker>
                ))}

                {/* Show tooltip on hover */}
                {[...groupedMarkers.filter((group) => group.items.length === 1).map((group) => group.items[0]), ...visibleUngroupedMarkers].map((item, index) => {
                    if (hoveredMarker && markerIdentity(hoveredMarker) === markerIdentity(item)) return null;

                    const isSpiderfiedMarker = Boolean(item._spiderSourceIdentity);
                    const spiderSizeKey = isSpiderfiedMarker ? spiderBadgeSizeKey(item) : "";
                    const badgePosition = isSpiderfiedMarker
                        ? getSpiderBadgePosition(item, mapRef, spiderBadgeSizes[spiderSizeKey])
                        : calculatePopupPosition(item.latitude, item.longitude, mapRef);
                    const badgeOffset = isSpiderfiedMarker
                        ? badgePosition.offset
                        : [
                            badgePosition.offset[0],
                            badgePosition.anchor === "bottom" ? -42 : 10,
                        ];
                    const { primary, secondary } = getMarkerBadgeContent(item);

                    return (
                        <Popup
                            key={`marker-badge-${markerIdentity(item)}-${index}`}
                            latitude={item.latitude}
                            longitude={item.longitude}
                            anchor={badgePosition.anchor}
                            offset={badgeOffset}
                            closeButton={false}
                            closeOnClick={false}
                            className="map-marker-badge-popup"
                        >
                            <div
                                ref={
                                    isSpiderfiedMarker
                                        ? (node) => updateSpiderBadgeSize(node, spiderSizeKey, setSpiderBadgeSizes)
                                        : undefined
                                }
                                className="pointer-events-none bg-secondary border border-border rounded-full px-2.5 py-1 shadow-lg ring-1 ring-border backdrop-blur-sm"
                            >
                                <div className="text-text-primary text-xs font-medium leading-tight whitespace-nowrap">
                                    {primary}
                                </div>
                                {secondary && (
                                    <div className="text-text-muted text-[11px] leading-tight whitespace-nowrap">
                                        {secondary}
                                    </div>
                                )}
                            </div>
                        </Popup>
                    );
                })}

                {/* Show tooltip on hover */}
                {hoveredMarker && (() => {
                    const popupPosition = calculatePopupPosition(hoveredMarker.latitude, hoveredMarker.longitude, mapRef);
                    return (
                        <Popup
                            latitude={hoveredMarker.latitude}
                            longitude={hoveredMarker.longitude}
                            anchor={popupPosition.anchor}
                            offset={popupPosition.offset}
                            closeButton={false}
                        closeOnClick={false}
                        onClose={() => {
                            if (hoverTimeoutRef.current) {
                                clearTimeout(hoverTimeoutRef.current);
                                hoverTimeoutRef.current = null;
                            }
                            setHoveredMarker(null);
                        }}
                        className="map-marker-popup"
                    >
                        <div
                            onMouseEnter={() => {
                                // Clear any pending hide timeout when mouse enters popup
                                if (hoverTimeoutRef.current) {
                                    clearTimeout(hoverTimeoutRef.current);
                                    hoverTimeoutRef.current = null;
                                }
                            }}
                            onMouseLeave={() => {
                                // Hide popup when mouse leaves popup area
                                hoverTimeoutRef.current = setTimeout(() => {
                                    setHoveredMarker(null);
                                    hoverTimeoutRef.current = null;
                                }, 100);
                            }}
                        >
                            <Tooltip item={hoveredMarker} />
                        </div>
                        </Popup>
                    );
                })()}
                </Map>
            </div>
        </>
    );
};

export default FlatMap;

